import type { RuleBuilderTacticParams } from '@/entities/pricing-rule'
import type { DataOption } from '@/shared/components/HookForm/types'
import type {
	CatalogParamModel,
	RuleBasePricingTacticsModel,
} from '@/shared/generated'

import { startCase } from 'lodash/fp'
import { ChevronsUpDownIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useCompetitorsOptionsQuery } from '@/entities/core'
import {
	formatParamValue,
	resolveParamValue,
	usePricingRuleCatalogQuery,
} from '@/entities/pricing-rule'

import { Button } from '@/shared/components/ui/button'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { Spinner } from '@/shared/components/ui/spinner'
import { Tag } from '@/shared/components/ui/tag'

import { usePriceColumnOptions } from '../lib/usePriceColumnOptions'

type RuleBaseParams =
	RuleBasePricingTacticsModel['params'] | RuleBuilderTacticParams

const formatLegacyRule = (rule: any) => {
	if (rule.pricing_rule_id && rule.pricing_rule_version_id) {
		return rule
	} else {
		return {
			pricing_rule_id: rule.rule_id,
			pricing_rule_version_id: null,
			rule_params: rule.rule_params,
		}
	}
}

const humanize = (id: string) => startCase(id)

/**
 * Read-only renderer for the rule-builder RB tactic: the selected variant's name
 * plus one label/value row per param, with values resolved to display labels
 * (competitor ids → names, aggregation/enum → labels) via `formatParamValue`
 * (spec §5, Q3).
 */
export const PricingTacticRuleBaseView = ({
	settings,
}: {
	settings: RuleBaseParams
}) => {
	const { t } = useTranslation()
	const value = formatLegacyRule(settings)
	const { data, isLoading } = usePricingRuleCatalogQuery()
	const competitors = useCompetitorsOptionsQuery<DataOption[]>(
		{ limit: 0, order_by: 'name' },
		{
			select: (response) =>
				(response.objects ?? [])
					.filter((c) => !!c.competitor_id && !!c.name)
					.map((c) => ({ value: c.name!, label: c.name! })),
		}
	)

	const priceColumns = usePriceColumnOptions()

	if (isLoading || competitors.isLoading || priceColumns.isLoading) {
		return <Spinner className='opacity-50' />
	}

	const variant = data?.data.find((v) =>
		value.pricing_rule_version_id
			? v.id === value.pricing_rule_id &&
				v.version_id === value.pricing_rule_version_id
			: v.id === value.pricing_rule_id
	)
	const options = competitors.data ?? []

	return (
		<DescriptionList
			className='flex flex-col gap-3'
			data-testid='pr-tactic-view'
		>
			<DescriptionGroup className='flex flex-col'>
				<DescriptionTerm>
					{t('pr_tactic_rule', {
						defaultValue: 'Pricing rule',
					})}
				</DescriptionTerm>
				<DescriptionDetail
					className='font-semibold'
					data-testid='pr-tactic-view-name'
				>
					{variant?.name
						? t(variant.name, {
								defaultValue: variant.name,
							})
						: value.pricing_rule_id}
				</DescriptionDetail>
			</DescriptionGroup>
			{(variant?.params ?? []).map((param: CatalogParamModel) => {
				const shouldCollpsed = param.type === 'competitor_list'
				const label = param.label
					? t(param.label, {
							defaultValue: param.label,
						})
					: humanize(param.id)
				if (shouldCollpsed) {
					const resolvedValue = resolveParamValue(
						param,
						value.rule_params?.[param.id],
						{
							competitors: options,
						}
					) as string[]
					const items = resolvedValue.slice(0, 3)
					const rest = resolvedValue.slice(3)

					return (
						<DescriptionGroup
							key={param.id}
							className='flex flex-col'
							data-testid={`pr-tactic-view-param-${param.id}`}
						>
							<DescriptionTerm>{label}</DescriptionTerm>
							<DescriptionDetail>
								<Collapsible className='flex w-full flex-col gap-2'>
									<div className='flex items-center justify-between gap-4'>
										<div className='flex w-full flex-wrap items-center gap-2'>
											{items.map((v) => (
												<Tag key={v}>{v}</Tag>
											))}
											<span className='text-xs text-muted-foreground'>
												+ {rest.length}{' '}
												{t('more', {
													defaultValue: 'more',
												})}
											</span>
										</div>
										<CollapsibleTrigger asChild>
											<Button variant='ghost' size='icon'>
												<ChevronsUpDownIcon />
											</Button>
										</CollapsibleTrigger>
									</div>
									<CollapsibleContent className='flex w-full flex-wrap gap-2'>
										{rest.map((v) => (
											<Tag key={v}>{v}</Tag>
										))}
									</CollapsibleContent>
								</Collapsible>
							</DescriptionDetail>
						</DescriptionGroup>
					)
				}

				return (
					<DescriptionGroup
						key={param.id}
						className='flex flex-col'
						data-testid={`pr-tactic-view-param-${param.id}`}
					>
						<DescriptionTerm>{label}</DescriptionTerm>
						<DescriptionDetail className='font-semibold'>
							{formatParamValue(param, value.rule_params?.[param.id], {
								competitors: options,
								priceColumns: priceColumns.options,
							})}
						</DescriptionDetail>
					</DescriptionGroup>
				)
			})}
		</DescriptionList>
	)
}
